import { z } from "zod";

import { APP_LANGUAGES, DEFAULT_LANGUAGE } from "../languages";
import { NotificationAudienceType, NotificationCampaignStatus, NotificationChannel } from "./campaigns";

export const notificationChannelSchema = z.nativeEnum(NotificationChannel);
export type NotificationChannelValue = z.infer<typeof notificationChannelSchema>;

export const notificationAudienceTypeSchema = z.nativeEnum(NotificationAudienceType);
export type NotificationAudienceTypeValue = z.infer<typeof notificationAudienceTypeSchema>;

export const notificationCampaignStatusSchema = z.nativeEnum(NotificationCampaignStatus);
export type NotificationCampaignStatusValue = z.infer<typeof notificationCampaignStatusSchema>;

export const campaignRecipientSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
});
export type CampaignRecipient = z.infer<typeof campaignRecipientSchema>;

/**
 * Audience selector. `type` discriminates the targeting:
 * - `all`: every user, resolved lazily per-user at read time (no enumeration)
 * - `organization`: every member of `organizationId`, resolved lazily at read time
 * - `users`: the explicit recipients the admin picked from the paginated user list
 *
 * Only `users` carries recipient identities up front. `all` and `organization`
 * never list members: each user materializes their own copy when they next open
 * their inbox, so the pack stays agnostic to whichever starter (and auth engine)
 * is installed.
 */
export const sendCampaignAudienceSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(NotificationAudienceType.ALL),
  }),
  z.object({
    type: z.literal(NotificationAudienceType.ORGANIZATION),
    organizationId: z.string().min(1),
  }),
  z.object({
    type: z.literal(NotificationAudienceType.USERS),
    recipients: z.array(campaignRecipientSchema).min(1),
  }),
]);
export type SendCampaignAudience = z.infer<typeof sendCampaignAudienceSchema>;

/** A broadcast's words in one language. */
export const campaignTextSchema = z.object({
  title: z.string().min(1).max(200),
  body: z.string().max(2000).default(""),
});
export type CampaignText = z.infer<typeof campaignTextSchema>;

/**
 * The broadcast in each app language other than the default one, whose words are `title` and
 * `body`. Every recipient reads the one in the language saved on their account; anyone without
 * one, or a language left out here, gets the default.
 */
export const campaignTranslationsSchema = z.partialRecord(
  z.enum(APP_LANGUAGES).exclude([DEFAULT_LANGUAGE]),
  campaignTextSchema,
);
export type CampaignTranslations = z.infer<typeof campaignTranslationsSchema>;

export const sendCampaignSchema = z
  .object({
    type: z.string().min(1).max(128),
    title: z.string().min(1).max(200),
    body: z.string().max(2000).default(""),
    translations: campaignTranslationsSchema.default({}),
    metadata: z.record(z.string(), z.unknown()).optional(),
    channels: z.array(notificationChannelSchema).min(1),
    audience: sendCampaignAudienceSchema,
  })
  // Email needs concrete addresses, which only the `users` audience carries.
  // Broadcasts (`all` / `organization`) are resolved lazily per-user and never
  // enumerate members, so they are in-app only.
  .refine(
    (value) =>
      !value.channels.includes(NotificationChannel.EMAIL) || value.audience.type === NotificationAudienceType.USERS,
    {
      message: "Email delivery is only available when specific users are selected",
      path: ["channels"],
    },
  );
export type SendCampaignInput = z.infer<typeof sendCampaignSchema>;

export const campaignRecordSchema = z.object({
  id: z.string().uuid(),
  actorId: z.string(),
  actorEmail: z.string(),
  type: z.string(),
  title: z.string(),
  body: z.string(),
  translations: campaignTranslationsSchema,
  metadata: z.record(z.string(), z.unknown()),
  channels: z.array(notificationChannelSchema),
  audienceType: notificationAudienceTypeSchema,
  audienceOrganizationId: z.string().nullable(),
  audienceUserIds: z.array(z.string()),
  recipientCount: z.number().int().min(0),
  readCount: z.number().int().min(0),
  readRate: z.number().min(0).max(1),
  emailSentCount: z.number().int().min(0),
  status: notificationCampaignStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CampaignRecord = z.infer<typeof campaignRecordSchema>;

export const campaignStatsSchema = z.object({
  recipientCount: z.number().int().min(0),
  readCount: z.number().int().min(0),
  readRate: z.number().min(0).max(1),
  emailSentCount: z.number().int().min(0),
});
export type CampaignStats = z.infer<typeof campaignStatsSchema>;

export const listCampaignsParamsSchema = z.object({
  limit: z.coerce.number().int().positive().max(100).optional(),
  after: z.string().optional(),
});
export type ListCampaignsParams = z.infer<typeof listCampaignsParamsSchema>;

export const notificationSendPointSchema = z.object({
  date: z.string(),
  /** In-app deliveries billed that day. */
  app: z.number().int().min(0),
  /** Email deliveries billed that day. */
  email: z.number().int().min(0),
});
export type NotificationSendPoint = z.infer<typeof notificationSendPointSchema>;

export const notificationStatsSchema = z.object({
  totalCampaigns: z.number().int().min(0),
  totalRecipients: z.number().int().min(0),
  averageReadRate: z.number().min(0).max(1),
  sends: z.array(notificationSendPointSchema),
});
export type NotificationStats = z.infer<typeof notificationStatsSchema>;
